import React, { useState } from "react"
import HoverTitle from "../../../GlobalComponents/HoverTitle/HoverTitle"
import PrimaryButton from "../../../GlobalComponents/PrimaryButton/PrimaryButton"
import { highlightTitleHtml } from "../../../../utils/highlightTitleHtml"
import * as styles from "./MemberVideos.module.css"

const extractVideoId = url => {
  if (!url) return ""
  if (url.length === 11 && !/[/?&]/.test(url)) return url
  const match = String(url).match(
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/,
  )
  return match ? match[1] : ""
}

const getMediaUrl = media =>
  media?.node?.publicUrl || media?.node?.sourceUrl || null

const buildEaasItems = data => {
  const items = []
  const {
    testimonialFirstYoutubeVideoUrlEaas,
    testimonialFirstYoutubeVideoImageEaas,
    testimonialFirstYoutubeVideoTitle,
    testimonialFirstYoutubeVideoDescription,
    testimonialsEaas = [],
  } = data || {}

  if (
    testimonialFirstYoutubeVideoUrlEaas ||
    testimonialFirstYoutubeVideoTitle ||
    testimonialFirstYoutubeVideoImageEaas
  ) {
    items.push({
      videoTitle: testimonialFirstYoutubeVideoTitle,
      description: testimonialFirstYoutubeVideoDescription,
      youtubeVideoUrl: testimonialFirstYoutubeVideoUrlEaas || "",
      poster: getMediaUrl(testimonialFirstYoutubeVideoImageEaas),
    })
  }

  ;(testimonialsEaas || []).forEach(item => {
    items.push({
      videoTitle: item?.videoTitle,
      description: item?.videoDescription,
      youtubeVideoUrl: item?.youtubeVideoUrl || "",
      poster: getMediaUrl(item?.youtubeVideoImage),
    })
  })

  return items
}

const buildSecItems = data =>
  (data?.videoSectionVideos || []).map(item => ({
    videoTitle: item?.videoTitle,
    description: null,
    youtubeVideoUrl: item?.videoYoutubeUrl || "",
    poster: getMediaUrl(item?.videoPreviewImage),
  }))

const MembersVideos = ({ data, hideCta = false }) => {
  const isSec = Boolean(
    data?.videoSectionTitle ||
      data?.videoSectionDescription ||
      data?.videoSectionVideos?.length,
  )

  const testimonialTitle = isSec
    ? data?.videoSectionTitle
    : data?.eaasWhatMembersAreSaying
  const testimonialText = isSec
    ? data?.videoSectionDescription
    : data?.eaasWhatMembersAreSayingDescription

  const items = isSec ? buildSecItems(data) : buildEaasItems(data)
  const visibleItems = items
    .filter(item => item.youtubeVideoUrl || item.videoTitle || item.poster)
    .slice(0, 2)

  const [playing, setPlaying] = useState(null)

  if (!visibleItems.length && !testimonialTitle && !testimonialText) return null
  if (!visibleItems.length) return null

  const titleHtml = highlightTitleHtml(
    testimonialTitle,
    "What Our Members",
    "What Our Members Are Saying",
  )

  const ctaLabel =
    data?.eaasMembersLearnMoreCtaButton?.title || "See All Testimonials"
  const ctaTo = data?.eaasMembersLearnMoreCtaButton?.url || "/testimonials"
  const showCta = !hideCta

  return (
    <section className={styles.section}>
      <div className="container">
        <HoverTitle html={titleHtml} className={`${styles.title} h2-medium`} />
        {testimonialText && (
          <p className={`${styles.intro} body-large-regular`}>
            {testimonialText}
          </p>
        )}

        <div className={styles.grid}>
          {visibleItems.map((item, index) => {
            const videoId = extractVideoId(item.youtubeVideoUrl)
            const poster =
              item.poster ||
              (videoId
                ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
                : null)
            const isPlaying = playing === index

            return (
              <article key={index} className={styles.card}>
                <div className={styles.player}>
                  {isPlaying && videoId ? (
                    <iframe
                      title={item.videoTitle || `Testimonial ${index + 1}`}
                      src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&showinfo=0`}
                      className={styles.iframe}
                      referrerPolicy="strict-origin-when-cross-origin"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <div className={styles.thumbWrap}>
                      {poster ? (
                        <img
                          src={poster}
                          alt={item.videoTitle || ""}
                          className={styles.thumb}
                          draggable={false}
                        />
                      ) : (
                        <div className={styles.thumbPlaceholder} />
                      )}
                      {videoId && (
                        <button
                          type="button"
                          className={styles.playBtn}
                          onClick={() => setPlaying(index)}
                          aria-label={`Play ${item.videoTitle || "video"}`}
                        >
                          <span className={styles.playArrow} aria-hidden="true">
                            ▶
                          </span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {item.videoTitle && (
                  <h3 className={`${styles.cardTitle} body-medium-medium`}>
                    {item.videoTitle}
                  </h3>
                )}
                {item.description && (
                  <p className={`${styles.cardDescr} body-medium-regular`}>
                    {item.description}
                  </p>
                )}
              </article>
            )
          })}
        </div>

        {showCta && (
          <div className={styles.cta}>
            <PrimaryButton to={ctaTo}>{ctaLabel}</PrimaryButton>
          </div>
        )}
      </div>
    </section>
  )
}

export default MembersVideos
